<template>
  <div class="bg-gray-f7">
    <notification-message v-if="hasHeaderInformationsData" :header-informations="headerInformationsForTopPage" />
    <search-page v-if="isSearchKeywordPage" />
    <top-page v-else :top-data="topData" :ads="ads" />
  </div>
</template>

<script>
import { useMainStore } from '@/store'
import CriteoGtmData from '@/utils/CriteoGtmData'
import AdvertisementData from '@/utils/AdvertisementData'
import SearchData from '@/utils/search/SearchData'
import GTM from '@/utils/tracking/gtm'
import { monitorCLS } from '@/utils/monitoring/ClsMonitor'
import SearchPage from '@/pages/search'
const TopPage = defineAsyncComponent(() => import('@/components/top/TopPage.vue'))
const NotificationMessage = defineAsyncComponent(() => import('@/components/NotificationMessage.vue'))

export default defineNuxtComponent({
  components: {
    SearchPage,
    TopPage,
    NotificationMessage
  },
  async setup() {
    const app = useNuxtApp()
    const store = useMainStore()
    const route = useRoute()
    useHeadParams({ $route: route })
    const searchData = ref({})
    const isSearchKeywordPage = ref(false)
    const topData = ref({})
    const ads = ref({})
    // The page has path like 「machicon.jp/?s=xxx」 is the search page with keyword

    const fetchData = async () => {
      if (route.query.s) {
        searchData.value = await new SearchData(store, route).fetchData().catch(async e => {
          const errorCode = parseInt(e.response && e.response.status)
          await app.$error(errorCode)
        })
        isSearchKeywordPage.value = true
        topData.value = {}
        ads.value = {}
        if (import.meta.client) window.scrollTo(0, 0)
        return
      }

      const requests = []
      const adParams = {
        top_banner: true,
        featured: true,
        recommendations: true,
        tie_ups: true,
        attention_event: true,
        fv_top_banner: true
      }
      const advertisementData = new AdvertisementData(store, adParams)
      const contentRequest = store.dispatch('apiGetV2', 'page_datas/top').then(res => {
        topData.value = {
          prefectureRanking: res.data.prefecture_ranking,
          regionRankings: res.data.region_rankings,
          information: res.data.information,
          publications: res.data.publications,
          marketInsights: res.data.market_insights,
          headerInformations: res.data.header_informations
        }
        isSearchKeywordPage.value = false
      })
      requests.push(contentRequest)
      requests.push(advertisementData.fetchData())
      requests.push(new CriteoGtmData(store).fetchData())

      try {
        const [mainContents, bannerContents] = await Promise.all(requests)
        ads.value = bannerContents.ads
        return { ...mainContents, ...bannerContents }
      } catch (e) {
        const errorCode = parseInt(e.response && e.response.status)
        await app.$error(errorCode)
      }
      if (import.meta.client) window.scrollTo(0, 0)
    }

    await fetchData()
    watch(
      () => [route.query.page, route.query.s],
      async () => {
        await fetchData()
      }
    )
    return {
      searchData,
      isSearchKeywordPage,
      topData,
      ads
    }
  },
  data: function () {
    return {
      isSearchKeywordPage: false,
      topData: {},
      ads: {},
      clsMonitorCleanup: null
    }
  },
  computed: {
    criteoGtmData() {
      return this.$store.$state.criteo_gtm_data
    },
    headerInformationsForTopPage() {
      return this.topData?.headerInformations?.filter(item => item.show_header === true)
    },
    hasHeaderInformationsData() {
      return this.headerInformationsForTopPage?.length > 0
    }
  },
  mounted() {
    // push GTM data for home page (exclude search by keyword page /?s=xxx)
    if (!this.$route.query.s) this.pushGtmData()
    this.clsMonitorCleanup = monitorCLS()
  },
  unmounted() {
    if (this.clsMonitorCleanup) this.clsMonitorCleanup()
  },
  methods: {
    pushGtmData() {
      const gtm = new GTM(this.$route, this.$store)
      GTM.pushData(gtm.defaultData)
      GTM.pushData({
        PageType: 'Homepage',
        HashedEmail: this.criteoGtmData.hashed_email,
        Ui_userlv: this.criteoGtmData.ui_userlv
      })
      GTM.trackingPageView(this.$route)
    }
  }
})
</script>

<style lang="scss" scoped>
.bg-gray-f7 {
  background-color: $gray-f7;
}
</style>
